import { useCallback, useEffect, useRef, useState } from "react";
import { clientLogger } from "../../../utils/clientLogger";
import {
  FilterConfig,
  applyLiveNoise,
  buildInputFilterChain,
} from "../../../utils/telephonyFilters";

/**
 * WebSocket Audio Manager — mic capture + WebSocket transport only.
 * Playback is handled separately by AudioManager.
 *
 * Key design for AEC (modeled after voice-test.html):
 * 1. Mic AudioContext at NATIVE sample rate — preserves browser AEC pipeline
 * 2. Resample native→TARGET_SAMPLE_RATE in JS (not by forcing AudioContext sampleRate)
 * 3. ScriptProcessor buffer 4096 (not 512) — gives AEC enough samples
 * 4. echoCancellation: true (strict, not { ideal: true })
 *
 * Optional `filterConfig` splices a telephony-style filter chain
 * (bandpass + AGC + µ-law + line noise) between the mic source and
 * the ScriptProcessor — same chain as `/phone-sim`. Filter graph is
 * captured at connect time; only the noise gain is reactive.
 */

interface WebSocketAudioManagerProps {
  onAudioReceived?: (audioData: ArrayBuffer) => void;
  onStreamEvent?: (eventData: any) => void;
  playbackStream?: MediaStream | null;
  isListening?: boolean;
  isThinking?: boolean;
  isSpeaking?: boolean;
  onConnectError?: () => void;
  onConnectionStart?: () => void;
  onConnectionComplete?: () => void;
  language?: string;
  useCase?: string;
  /** "ai" = v1 (STT→LLM), "portal" = v2 (human operator) */
  mode?: "ai" | "portal";
  /** Optional telephony filter chain config (Settings → Phone simulation). */
  filterConfig?: FilterConfig | null;
  /** Sample rate sent to server (mic uplink). Default 16000. */
  inputSampleRate?: number;
  /**
   * Per-call Khmer STT model: "v1" (model 1, default) or "v5" (model 2).
   * Sent as the `stt_model` query param; omitted when empty/"v1" so the
   * backend applies its own default (KHMER_STT_MODEL).
   */
  sttModel?: string;
  /**
   * Suppress the bot's own audio leaking back up the mic while it speaks.
   * Default on. Turn off to measure the raw leak, or if a deployment has
   * hardware echo cancellation (handset / headset) making it redundant.
   */
  echoGuard?: boolean;
}

const DEFAULT_TARGET_SAMPLE_RATE = 16000;

/**
 * Canonical disconnect causes. Exactly one `session_disconnect_reason`
 * event is emitted per session, with `cause` set to one of these values,
 * before the WebSocket close completes. Add new variants here when a new
 * close path is introduced — the union forces every call site to be
 * categorised, so post-mortem log analysis can grep one event and know
 * unambiguously why the call ended.
 */
export type DisconnectCause =
  // Intentional (call site explicitly asked to close)
  | "user_hangup"           // user clicked the hang-up / stop button
  | "idle_timeout"          // UI inactivity countdown (CountdownEffect) expired
  | "survey_complete"       // bot signalled session.close after survey done
  | "page_unload"           // pagehide / beforeunload while WS still open
  | "navigate_away"         // SPA route change while WS still open
  // Unintentional (server, network, hardware, browser)
  | "server_close"          // server pushed session.close / disconnect message
  | "ws_error"              // WebSocket onerror fired (transport-level)
  | "ws_abnormal_close"     // WebSocket onclose with non-1000/1001 code, not intentional
  | "mic_lost"              // mic track ended mid-call (permission revoked / device unplugged)
  | "network_offline"       // browser fired `offline` event while WS open
  | "connect_failed"        // initial WebSocket connect did not reach OPEN
  | "unknown";              // fallback — should never appear in healthy logs

/** Linear interpolation resample (same as voice-test.html) */
function resample(inputData: Float32Array, inputRate: number, outputRate: number): Float32Array {
  if (inputRate === outputRate) return inputData;
  const ratio = inputRate / outputRate;
  const outputLen = Math.round(inputData.length / ratio);
  const output = new Float32Array(outputLen);
  for (let i = 0; i < outputLen; i++) {
    const srcIdx = i * ratio;
    const idx = Math.floor(srcIdx);
    const frac = srcIdx - idx;
    output[i] =
      idx + 1 < inputData.length
        ? inputData[idx] * (1 - frac) + inputData[idx + 1] * frac
        : inputData[idx];
  }
  return output;
}

/** Float32 → PCM16 conversion */
function float32ToPCM16(float32: Float32Array): Int16Array {
  const pcm16 = new Int16Array(float32.length);
  for (let i = 0; i < float32.length; i++) {
    const s = Math.max(-1, Math.min(1, float32[i]));
    pcm16[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
  }
  return pcm16;
}

/**
 * ── Echo guard ────────────────────────────────────────────────────────────
 * Browser AEC alone does not stop the bot's own audio from leaking back up
 * the mic on this stack: playback lives in AudioManager's AudioContext while
 * capture lives in ours, and the `updatePlaybackStream` AEC-reference hook is
 * a no-op on both transports. The leak is not merely cosmetic — server-side
 * VAD latches onto it, so `input_audio_buffer.speech_started` fires during the
 * bot's own utterance and stays latched until after playback ends. The
 * backend then classifies the caller's REAL answer that follows as
 * `speech_started_before_bot_ended` and drops it (session usl25jw4fnl lost two
 * genuine answers this way: `ជួសជុលហើយ` and `បាទ`).
 *
 * So while the bot is speaking we forward mic audio only when it is clearly
 * louder than the echo currently leaking through. Design notes:
 *
 * • ATTENUATE, NEVER STOP SENDING. Suppressed frames are transmitted as
 *   silence, not dropped, so the server's audio clock stays continuous.
 * • The threshold adapts to the OBSERVED leak (speaker volume, room, headset
 *   vs laptop mic all differ) rather than a fixed constant, and only adapts on
 *   frames it already believes are echo — otherwise real speech would drag the
 *   floor up behind it and gate the rest of the sentence out.
 * • Real barge-in still gets through: speech into the mic sits far above the
 *   residual echo floor. This matters for the backend's
 *   `STT_KEEP_PAST_BOTEND_S` branch, which keeps an utterance that STARTED
 *   during playback if it continues past bot-end — a hard mute would truncate
 *   exactly that utterance's opening syllables.
 */
const ECHO_HANGOVER_MS = 300;      // keep guarding after playback ends (tail/jitter)
const ECHO_FLOOR_ATTACK = 0.9;     // EMA weight on the existing floor estimate
const ECHO_MARGIN = 3.5;           // real speech must exceed the floor by this factor
const ECHO_FLOOR_MIN = 0.002;      // never gate on a collapsed floor (quiet room)
const ECHO_FLOOR_DECAY = 0.95;     // floor relaxes back down once the bot is quiet
const ECHO_LEARN_FRAMES = 8;       // frames sampled at each utterance start to size the leak

function rmsOf(buf: Float32Array): number {
  let sum = 0;
  for (let i = 0; i < buf.length; i++) sum += buf[i] * buf[i];
  return Math.sqrt(sum / buf.length);
}

export const useWebSocketAudioManager = ({
  onAudioReceived,
  onStreamEvent,
  isListening = false,
  isThinking = false,
  isSpeaking = false,
  onConnectError,
  onConnectionStart,
  onConnectionComplete,
  language = "vi",
  useCase = "ha_scenario",
  mode = "ai",
  filterConfig = null,
  inputSampleRate = DEFAULT_TARGET_SAMPLE_RATE,
  sttModel = "",
  echoGuard = true,
}: WebSocketAudioManagerProps) => {
  const wsRef = useRef<WebSocket | null>(null);

  // MIC: AudioContext at native sample rate (for AEC to work)
  const micContextRef = useRef<AudioContext | null>(null);
  const sourceRef = useRef<MediaStreamAudioSourceNode | null>(null);
  const processorRef = useRef<ScriptProcessorNode | null>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  // Telephony filter chain handles (only set when filterConfig active).
  const filterNoiseSrcRef = useRef<AudioBufferSourceNode | null>(null);
  const filterNoiseGainRef = useRef<GainNode | null>(null);
  const filterConfigRef = useRef<FilterConfig | null>(filterConfig);
  // Live tunable: keep the noise gain in sync without rebuilding the graph.
  useEffect(() => {
    filterConfigRef.current = filterConfig;
    const ctx = micContextRef.current;
    if (ctx)
      applyLiveNoise(ctx, filterNoiseGainRef.current, filterConfig, "input");
  }, [filterConfig]);
  const inputSampleRateRef = useRef(inputSampleRate);
  useEffect(() => { inputSampleRateRef.current = inputSampleRate; }, [inputSampleRate]);

  // State refs
  const isConnectingRef = useRef(false);
  const intentionalDisconnectRef = useRef(false);
  const isListeningRef = useRef(isListening);
  const isThinkingRef = useRef(isThinking);
  const isSpeakingRef = useRef(isSpeaking);
  // Echo guard state (see ECHO_* constants above).
  const echoGuardRef = useRef(echoGuard);
  const echoFloorRef = useRef(ECHO_FLOOR_MIN);
  const lastBotAudioAtRef = useRef(0);
  const wasGuardingRef = useRef(false);
  const echoLearnLeftRef = useRef(0);
  const echoStatsRef = useRef({ suppressed: 0, passed: 0 });
  const [isConnected, setIsConnected] = useState(false);
  const [clientId, setClientId] = useState("");

  // Disconnect-reason bookkeeping. `disconnectReasonReportedRef` ensures
  // exactly one `session_disconnect_reason` event lands per session
  // regardless of how many close paths fire (e.g. user_hangup → ws.close
  // → ws.onclose all run; only the first reports). `wsOpenedAtRef`
  // captures uptime so the canonical event includes call duration.
  const disconnectReasonReportedRef = useRef(false);
  const wsOpenedAtRef = useRef<number | null>(null);

  /**
   * Emit the canonical `session_disconnect_reason` event. Idempotent —
   * subsequent calls are no-ops. Always emit BEFORE actually closing the
   * WebSocket (or as soon as you know the close is coming) so the line
   * lands in the .client.log next to the other close events.
   *
   * `cause` is the single source of truth for "why did the call end?".
   * `subcause` is free text for nuance (button id, error code, etc.).
   * `triggered_by` names the function/handler that decided to close.
   */
  const reportDisconnectReason = useCallback(
    (cause: DisconnectCause, subcause: string = "", triggered_by: string = "") => {
      if (disconnectReasonReportedRef.current) return;
      disconnectReasonReportedRef.current = true;
      const ws = wsRef.current;
      const wsState = ws?.readyState ?? null;
      const openedAt = wsOpenedAtRef.current;
      clientLogger.log("session_disconnect_reason", {
        cause,
        subcause,
        triggered_by,
        ws_ready_state: wsState,
        ws_open_ms: openedAt != null ? Date.now() - openedAt : null,
        browser_online: typeof navigator !== "undefined" ? navigator.onLine : null,
        page_visibility: typeof document !== "undefined" ? document.visibilityState : null,
        mic_track_state: mediaStreamRef.current?.getAudioTracks()[0]?.readyState ?? null,
        was_intentional: intentionalDisconnectRef.current,
      });
    },
    [],
  );

  // Callback refs
  const onStreamEventRef = useRef(onStreamEvent);
  const onAudioReceivedRef = useRef(onAudioReceived);

  useEffect(() => { onStreamEventRef.current = onStreamEvent; }, [onStreamEvent]);
  useEffect(() => { onAudioReceivedRef.current = onAudioReceived; }, [onAudioReceived]);
  useEffect(() => { isListeningRef.current = isListening; }, [isListening]);
  useEffect(() => { isThinkingRef.current = isThinking; }, [isThinking]);
  useEffect(() => { echoGuardRef.current = echoGuard; }, [echoGuard]);
  useEffect(() => {
    isSpeakingRef.current = isSpeaking;
    // Report the leak per bot turn: `suppressed` counts mic frames judged to be
    // the bot's own audio. A turn that ends with suppressed≈0 means the guard
    // saw no leak (good AEC, or `isSpeaking` arriving late); a turn that is
    // nearly all `passed` while the caller stayed silent means the floor is set
    // too low for this device and echo is still reaching the server's VAD.
    if (!isSpeaking) {
      const { suppressed, passed } = echoStatsRef.current;
      if (suppressed || passed) {
        clientLogger.log("echo_guard_turn", {
          suppressed_frames: suppressed,
          passed_frames: passed,
          echo_floor: Number(echoFloorRef.current.toFixed(5)),
          enabled: echoGuardRef.current,
        });
        echoStatsRef.current = { suppressed: 0, passed: 0 };
      }
    }
  }, [isSpeaking]);

  // ── WebSocket URL ─────────────────────────────────────────────────

  const getWebSocketUrl = useCallback(() => {
    // Fall back to the page origin when VITE_BACKEND_URL is unset (the .env is
    // gitignored, so a fresh build has no value → `undefined.includes` used to
    // throw here and crash the WS connect) or points at localhost while we're
    // served from a real host (prod). Mirrors resolveBackendUrl()'s `|| ""` guard.
    let backendUrl = import.meta.env.VITE_BACKEND_URL || "";
    if (!backendUrl || (backendUrl.includes("localhost") && !window.location.hostname.includes("localhost"))) {
      backendUrl = window.location.origin;
    }
    const baseUrl = backendUrl.replace(
      /^https?/,
      (protocol: string) => (protocol === "https" ? "wss" : "ws")
    );
    const newClientId = Math.random().toString(36).substring(2, 18);
    setClientId(newClientId);
    clientLogger.setEndpointBase(backendUrl);
    clientLogger.setSessionId(newClientId);
    clientLogger.log("ws_url_built", {
      client_id: newClientId,
      language,
      use_case: useCase,
      mode,
      input_sample_rate: inputSampleRate,
    });
    const sr = inputSampleRate;
    // Khmer-only deployment: always run the call in `km`. The bot's cached
    // greeting/q0 audio and STT (stt-khmer) are Khmer; sending any other
    // language (e.g. "vi"/"vi-VN") makes the greeting-clip lookup miss and the
    // bot opens silently. Force km regardless of the UI i18n language.
    const callLanguage = "km";
    // Per-call Khmer STT model. Only appended for a non-default checkpoint
    // (v5 / v4) so a normal call keeps the backend's KHMER_STT_MODEL default.
    const modelParam =
      sttModel && sttModel !== "v1"
        ? `&stt_model=${encodeURIComponent(sttModel)}`
        : "";
    const wsPath = mode === "portal"
      ? `/v2/call/ws?client_id=${newClientId}&language=${callLanguage}&use_case=${useCase}&input_audio_sample_rate=${sr}${modelParam}`
      : `/v1/voice-bot/ws?client_id=${newClientId}&language=${callLanguage}&use_case=${useCase}&input_audio_sample_rate=${sr}${modelParam}`;
    return `${baseUrl}${wsPath}`;
  }, [language, useCase, mode, inputSampleRate, sttModel]);

  // ── Mic cleanup ───────────────────────────────────────────────────

  const cleanupAudioNodes = useCallback(() => {
    if (processorRef.current) {
      try {
        processorRef.current.onaudioprocess = null;
        processorRef.current.disconnect();
      } catch (e) { /* ignore */ }
      processorRef.current = null;
    }
    if (sourceRef.current) {
      try { sourceRef.current.disconnect(); } catch (e) { /* ignore */ }
      sourceRef.current = null;
    }
    if (filterNoiseSrcRef.current) {
      try { filterNoiseSrcRef.current.stop(); } catch { /* ignore */ }
      filterNoiseSrcRef.current = null;
    }
    filterNoiseGainRef.current = null;
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach((t) => t.stop());
      mediaStreamRef.current = null;
    }
    if (micContextRef.current) {
      try { micContextRef.current.close(); } catch (e) { /* ignore */ }
      micContextRef.current = null;
    }
  }, []);

  // ── Mic setup (native rate + JS resample, like voice-test.html) ───

  const setupAudioProcessing = useCallback(async () => {
    // IDEMPOTENT. Tearing the mic chain down and rebuilding it restarts the
    // browser's acoustic echo canceller, which then re-converges from cold and
    // leaks the first ~0.35-0.45s of whatever the bot is playing straight back
    // into the mic. That leak is what the server's energy VAD hears as "the
    // caller is speaking" (session ll8en1xoyqe: every mic burst sat inside a bot
    // utterance, offset by a near-constant ~0.4s).
    //
    // Measured before this guard: `audio_context_created` fired once per
    // `playback_completed` — i.e. once per bot turn — across every session
    // (whqul6p72h 7/5, ll8en1xoyqe 9/7, m2kuzkzhbc 14/12, 66s9dl60zst 17/15),
    // with only ONE `ws_connecting`. So something re-invokes this per turn.
    // Rather than depend on finding every caller, make a repeat call a no-op
    // while the existing chain is healthy: the AEC then converges once and
    // stays converged for the whole call.
    const liveTrack = mediaStreamRef.current?.getAudioTracks()?.[0];
    if (
      micContextRef.current &&
      micContextRef.current.state !== "closed" &&
      processorRef.current &&
      liveTrack &&
      liveTrack.readyState === "live"
    ) {
      // A suspended context still holds the same AEC session — resume rather
      // than rebuild (iOS Safari suspends on interruptions/backgrounding).
      if (micContextRef.current.state === "suspended") {
        try {
          await micContextRef.current.resume();
        } catch {
          /* fall through to rebuild below */
        }
      }
      if (micContextRef.current.state === "running") {
        clientLogger.log("audio_context_reused", {
          sample_rate: micContextRef.current.sampleRate,
          target_rate: inputSampleRateRef.current,
        });
        return;
      }
    }

    cleanupAudioNodes();

    // getUserMedia with strict AEC (true, not { ideal: true })
    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          channelCount: 1,
          echoCancellation: true,
          autoGainControl: true,
          noiseSuppression: true,
        },
      });
    } catch (err) {
      clientLogger.error("getusermedia_failed", {
        error_name: (err as any)?.name,
        error_message: (err as any)?.message,
      });
      throw err;
    }

    mediaStreamRef.current = stream;
    // Detect mic disconnect mid-call (user unplugs headset, OS revokes
    // permission, hardware glitch). Track end fires once and is a strong
    // signal for "user did not hang up but mic stream died".
    stream.getTracks().forEach((track) => {
      track.onended = () => {
        clientLogger.warn("mic_track_ended", {
          kind: track.kind,
          label: track.label,
          ready_state: track.readyState,
        });
        // If the WebSocket is still open when the mic dies, the resulting
        // close will look like a user hangup unless we record the cause.
        if (wsRef.current?.readyState === WebSocket.OPEN) {
          reportDisconnectReason("mic_lost", `track=${track.kind}`, "mic_track_ended");
        }
      };
      track.onmute = () => clientLogger.warn("mic_track_muted", { kind: track.kind });
      track.onunmute = () => clientLogger.log("mic_track_unmuted", { kind: track.kind });
    });

    // AudioContext at NATIVE rate — critical for browser AEC to work
    micContextRef.current = new (window.AudioContext ||
      (window as any).webkitAudioContext)();
    const nativeRate = micContextRef.current.sampleRate;
    console.log(`🎤 Mic native rate: ${nativeRate}Hz → resampling to ${inputSampleRateRef.current}Hz`);
    clientLogger.log("audio_context_created", {
      sample_rate: nativeRate,
      target_rate: inputSampleRateRef.current,
      state: micContextRef.current.state,
    });
    micContextRef.current.onstatechange = () => {
      const state = micContextRef.current?.state;
      // suspended state often means iOS Safari needs a user gesture to resume
      const level = state === "suspended" || state === "closed" ? "WARN" : "INFO";
      const fn = level === "WARN" ? clientLogger.warn : clientLogger.log;
      fn.call(clientLogger, "audio_context_state", { state });
    };

    sourceRef.current = micContextRef.current.createMediaStreamSource(stream);
    // Buffer 4096 (like voice-test.html) — gives AEC enough samples to work
    processorRef.current = micContextRef.current.createScriptProcessor(4096, 1, 1);

    // Insert telephony filter chain (Settings → Phone simulation) between
    // mic source and ScriptProcessor when configured. When `filterConfig`
    // is null/off the chain is a passthrough GainNode, no effect.
    const cfg = filterConfigRef.current;
    const inputChain = buildInputFilterChain(micContextRef.current, cfg);
    filterNoiseSrcRef.current = inputChain.noiseSource;
    filterNoiseGainRef.current = inputChain.noiseGain;
    sourceRef.current.connect(inputChain.entry);
    const chainExit = inputChain.exit;
    chainExit.connect(processorRef.current);

    let silentFrame: Int16Array | null = null;
    processorRef.current.onaudioprocess = (e) => {
      // Always stream mic PCM while the socket is open. Previously this was
      // also gated on isListeningRef (= isConnected && wakeword.isListening);
      // when the wakeword ONNX models fail to load (their /wakeword/models/*.onnx
      // return 502) wakeword.isListening stays false forever, so NO mic audio
      // was ever sent → the backend's user channel was silent → VAD/STT never
      // ran → survey auto-completed empty. Server-side VAD + turn-taking
      // (STT_CAPTURE_DURING_BOT_SPEECH + overlap gate + AEC) own turn logic, so
      // the client just sends continuously whenever the WS is up. The echo
      // guard below preserves that: it substitutes silence for frames that are
      // the bot's own audio, and never stops sending.
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        const inputData = e.inputBuffer.getChannelData(0);
        const resampled = resample(inputData, nativeRate, inputSampleRateRef.current);
        const pcm16 = float32ToPCM16(resampled);

        // ── Echo guard ──
        const now = performance.now();
        if (isSpeakingRef.current) lastBotAudioAtRef.current = now;
        const guarding =
          echoGuardRef.current &&
          (isSpeakingRef.current ||
            now - lastBotAudioAtRef.current < ECHO_HANGOVER_MS);
        const wasGuarding = wasGuardingRef.current;
        wasGuardingRef.current = guarding;

        if (guarding) {
          const level = rmsOf(inputData);

          // MEASURE phase, at the start of each bot utterance. The floor cannot
          // be learned from gated frames alone — a leak already louder than the
          // opening threshold would pass every frame and never train it, and the
          // guard would silently do nothing. So take the first few frames of the
          // bot's turn as a direct sample of the leak: the caller is virtually
          // never already talking at prompt onset, and the backend ignores
          // barge-in before `_REASK_SPOKEN_THRESHOLD` (0.8 of the prompt) anyway.
          if (!wasGuarding) {
            echoLearnLeftRef.current = ECHO_LEARN_FRAMES;
            echoFloorRef.current = ECHO_FLOOR_MIN;
          }
          if (echoLearnLeftRef.current > 0) {
            echoLearnLeftRef.current--;
            echoFloorRef.current = Math.max(echoFloorRef.current, level);
            echoStatsRef.current.suppressed++;
            if (silentFrame === null || silentFrame.length !== pcm16.length) {
              silentFrame = new Int16Array(pcm16.length);
            }
            wsRef.current.send(silentFrame.buffer);
            return;
          }

          const threshold = Math.max(ECHO_FLOOR_MIN, echoFloorRef.current) * ECHO_MARGIN;
          if (level < threshold) {
            // Believed to be the bot's own audio leaking back. Learn from it,
            // then send silence — dropping the frame instead would leave a hole
            // in the server's audio timeline.
            echoFloorRef.current =
              ECHO_FLOOR_ATTACK * echoFloorRef.current + (1 - ECHO_FLOOR_ATTACK) * level;
            echoStatsRef.current.suppressed++;
            // Reused zero buffer — this path runs every 4096 frames for the
            // whole of every bot utterance, so don't allocate per frame.
            if (silentFrame === null || silentFrame.length !== pcm16.length) {
              silentFrame = new Int16Array(pcm16.length);
            }
            wsRef.current.send(silentFrame.buffer);
            return;
          }
          // Loud enough to be the caller talking over the bot — pass it through
          // WITHOUT adapting the floor, so a real sentence can't raise the
          // threshold above its own remainder and gate itself out mid-word.
          echoStatsRef.current.passed++;
        } else if (echoFloorRef.current > ECHO_FLOOR_MIN) {
          // Bot quiet: relax the floor so a loud prompt doesn't keep the gate
          // high over the caller's answer that follows.
          echoFloorRef.current = Math.max(
            ECHO_FLOOR_MIN,
            echoFloorRef.current * ECHO_FLOOR_DECAY,
          );
        }

        wsRef.current.send(pcm16.buffer);
      }
    };

    processorRef.current.connect(micContextRef.current.destination);
  }, [cleanupAudioNodes]);

  // ── Connect ───────────────────────────────────────────────────────

  const connect = useCallback(async () => {
    // Setup mic FIRST (user gesture context for iOS Safari)
    try {
      await setupAudioProcessing();
    } catch (err) {
      console.error("❌ Audio setup failed:", err);
      throw new Error("Audio setup failed: " + (err as Error).message);
    }

    return new Promise<void>((resolve, reject) => {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        setIsConnected(true);
        resolve();
        return;
      }

      if (wsRef.current?.readyState === WebSocket.CONNECTING) {
        const checkConnection = setInterval(() => {
          const state = wsRef.current?.readyState;
          if (state === WebSocket.OPEN) {
            clearInterval(checkConnection);
            setIsConnected(true);
            resolve();
          } else if (state === WebSocket.CLOSED || state === undefined) {
            clearInterval(checkConnection);
            setIsConnected(false);
            reject(new Error("WebSocket connection failed"));
          }
        }, 100);
        setTimeout(() => {
          clearInterval(checkConnection);
          if (wsRef.current?.readyState !== WebSocket.OPEN) {
            reject(new Error("WebSocket connection timeout"));
          }
        }, 10000);
        return;
      }

      try {
        const wsUrl = getWebSocketUrl();
        clientLogger.log("ws_connecting", { url: wsUrl });
        const ws = new WebSocket(wsUrl);
        wsRef.current = ws;

        if (!isConnectingRef.current) {
          isConnectingRef.current = true;
          onConnectionStart?.();
        }

        ws.onopen = () => {
          setIsConnected(true);
          isConnectingRef.current = false;
          // Reset per-session bookkeeping — every fresh connection starts a
          // new "session" from the disconnect-reporter's POV.
          disconnectReasonReportedRef.current = false;
          wsOpenedAtRef.current = Date.now();
          clientLogger.log("ws_open", {});
          onConnectionComplete?.();
          resolve();
        };

        ws.onmessage = (event) => {
          if (event.data instanceof ArrayBuffer) {
            onAudioReceivedRef.current?.(event.data);
          } else if (event.data instanceof Blob) {
            event.data.arrayBuffer().then((ab) => onAudioReceivedRef.current?.(ab));
          } else {
            const text = typeof event.data === "string" ? event.data : "";
            if (text.trim().startsWith("{")) {
              try {
                const parsed = JSON.parse(text);
                // Surface server-initiated errors / closures so we can
                // tell whether a "user disconnected" was actually the
                // server kicking us out.
                const t = parsed.type ?? parsed.event_type ?? "";
                if (t === "error") {
                  clientLogger.error("ws_recv_server_error", {
                    type: t,
                    error: parsed.error ?? parsed.message,
                  });
                } else if (t === "session.close" || t === "session.closed" || t === "disconnect") {
                  clientLogger.warn("ws_recv_server_close", {
                    type: t,
                    reason: parsed.reason,
                  });
                  // Server told us the session is over — record cause now,
                  // before our subsequent ws.close handshake fires onclose.
                  reportDisconnectReason(
                    "server_close",
                    `type=${t} reason=${parsed.reason ?? ""}`,
                    "ws_recv_server_close",
                  );
                }
                onStreamEventRef.current?.(parsed);
                return;
              } catch (e) {
                clientLogger.warn("ws_recv_parse_error", {
                  preview: text.slice(0, 120),
                  error: (e as any)?.message,
                });
              }
            }
            for (const line of text.split("\n")) {
              if (line.startsWith("data: ")) {
                try { onStreamEventRef.current?.(JSON.parse(line.substring(6))); } catch (e) { /* ignore */ }
              }
            }
          }
        };

        let errorHandled = false;

        ws.onerror = (error) => {
          console.error("❌ WebSocket error:", error);
          clientLogger.log("ws_error", {
            ready_state: ws.readyState,
            message: (error as any)?.message ?? String(error),
          });
          // Distinguish "never connected" from "transport error mid-call".
          // Both are unintentional but their root causes (DNS/TLS/handshake
          // vs flaky network/server crash) need different investigation.
          const cause: DisconnectCause = wsOpenedAtRef.current === null
            ? "connect_failed"
            : "ws_error";
          reportDisconnectReason(
            cause,
            (error as any)?.message ?? "",
            "ws.onerror",
          );
          isConnectingRef.current = false;
          setIsConnected(false);
          if (!errorHandled) { errorHandled = true; onConnectError?.(); }
          reject(new Error("WebSocket connection error"));
        };

        ws.onclose = (event) => {
          console.log("🔌 WebSocket closed:", event.code, event.reason);
          const intentional = intentionalDisconnectRef.current;
          // Treat abnormal codes as WARN so they stand out in the .log
          const isAbnormal = event.code !== 1000 && event.code !== 1001;
          const data = {
            code: event.code,
            reason: event.reason,
            was_clean: event.wasClean,
            intentional,
            // Decode common close codes for at-a-glance debugging
            code_meaning:
              event.code === 1000 ? "normal" :
              event.code === 1001 ? "going_away" :
              event.code === 1005 ? "no_status" :
              event.code === 1006 ? "abnormal_no_close_frame" :
              event.code === 1011 ? "server_error" :
              event.code === 1012 ? "service_restart" :
              event.code === 1013 ? "try_again_later" :
              "other",
          };
          if (isAbnormal && !intentional) clientLogger.warn("ws_close", data);
          else clientLogger.log("ws_close", data);
          // Last chance to record a cause: any close path that didn't
          // already report (network drop, server crash without graceful
          // session.close, etc.) lands here. Infer best-effort from the
          // close code so the .client.log always carries one canonical
          // `session_disconnect_reason` entry per session.
          if (!disconnectReasonReportedRef.current) {
            const inferred: DisconnectCause = isAbnormal
              ? "ws_abnormal_close"
              : intentional
                ? "unknown"           // intentional but no caller tagged it
                : "ws_abnormal_close";
            reportDisconnectReason(
              inferred,
              `code=${event.code} reason=${event.reason ?? ""}`,
              "ws.onclose",
            );
          }
          // Final flush so the close event reaches the server even if
          // the page is teardown-bound right after.
          void clientLogger.flush(true);
          wsRef.current = null;
          isConnectingRef.current = false;
          setIsConnected(false);
          // Server-initiated graceful close (code 1000/1001) — e.g.
          // session.closed after survey_completed — is NOT an error.
          // Only escalate to onConnectError for abnormal closes.
          if (isAbnormal && !intentional && !errorHandled) {
            errorHandled = true;
            onConnectError?.();
          }
          intentionalDisconnectRef.current = false;
          if (event.code !== 1000) reject(new Error(`WebSocket closed with code ${event.code}`));
        };
      } catch (error) {
        onConnectError?.();
        reject(error);
      }
    });
  }, [getWebSocketUrl, onConnectionStart, onConnectionComplete, setupAudioProcessing, onConnectError]);

  // ── Disconnect ────────────────────────────────────────────────────

  /**
   * Intentional disconnect. `cause` is required and MUST be one of the
   * intentional `DisconnectCause` variants — passing an unintentional
   * variant (`ws_error`, `mic_lost`, etc.) would be misleading because
   * those paths report themselves and shouldn't be funnelled through
   * here. `subcause` carries free-text detail (button id, route name).
   *
   * Emits `session_disconnect_reason` BEFORE the WS close so the cause
   * lands in the .client.log first, ahead of `ws_disconnect_called` and
   * `ws_close`.
   */
  const disconnect = useCallback(
    (cause: "user_hangup" | "idle_timeout" | "survey_complete" | "page_unload" | "navigate_away",
     subcause: string = "") => {
      reportDisconnectReason(cause, subcause, "disconnect");
      if (wsRef.current) {
        intentionalDisconnectRef.current = true;
        clientLogger.log("ws_disconnect_called", {
          cause,
          subcause,
          ready_state: wsRef.current.readyState,
        });
        try {
          wsRef.current.send(JSON.stringify({ event_type: "disconnect", reason: cause }));
        } catch { /* ignore — socket may already be dead */ }
        wsRef.current.close(1000, cause);
        wsRef.current = null;
      }
      cleanupAudioNodes();
      setIsConnected(false);
    },
    [cleanupAudioNodes, reportDisconnectReason],
  );

  // ── Send event ────────────────────────────────────────────────────

  const sendEvent = useCallback((eventType: string, data?: any) => {
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
      console.warn(`⚠️ Cannot send "${eventType}" - WebSocket not open`);
      clientLogger.log("ws_send_blocked", {
        event_type: eventType,
        ready_state: wsRef.current?.readyState ?? "no_socket",
      });
      return;
    }
    try {
      wsRef.current.send(JSON.stringify({ event_type: eventType, data }));
      clientLogger.log("ws_send_event", { event_type: eventType });
    } catch (error) {
      console.error(`❌ Error sending event ${eventType}:`, error);
      clientLogger.log("ws_send_error", {
        event_type: eventType,
        error: (error as any)?.message ?? String(error),
      });
    }
  }, []);

  // ── State updates ─────────────────────────────────────────────────

  const updateListeningState = useCallback((listening: boolean) => {
    isListeningRef.current = listening;
    if (listening && wsRef.current?.readyState !== WebSocket.CONNECTING && !isConnectingRef.current) {
      connect().catch((error) => console.error("❌ Failed to connect:", error));
    }
  }, [connect]);

  const updateThinkingState = useCallback((thinking: boolean) => {
    isThinkingRef.current = thinking;
  }, []);

  const updatePlaybackStream = useCallback((_stream?: MediaStream | null) => {
    // No-op — AEC handled by separate AudioContexts (mic native rate vs playback in AudioManager)
  }, []);

  // ── Browser lifecycle: tag unintentional disconnects with the right cause
  // The clientLogger module hooks pagehide/visibility/online/offline at the
  // global level for buffer flushing, but it has no view of WS state. We
  // duplicate the listeners here only to record `session_disconnect_reason`
  // when the lifecycle event happens *while WS is still open* — i.e. it's
  // the actual cause of the imminent close.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const onOffline = () => {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        reportDisconnectReason("network_offline", "navigator.onLine=false", "online_offline_listener");
      }
    };
    const onPageHide = () => {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        reportDisconnectReason("page_unload", "pagehide", "pagehide_listener");
      }
    };
    const onBeforeUnload = () => {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        reportDisconnectReason("page_unload", "beforeunload", "beforeunload_listener");
      }
    };
    window.addEventListener("offline", onOffline);
    window.addEventListener("pagehide", onPageHide);
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => {
      window.removeEventListener("offline", onOffline);
      window.removeEventListener("pagehide", onPageHide);
      window.removeEventListener("beforeunload", onBeforeUnload);
    };
  }, [reportDisconnectReason]);

  return {
    connect,
    disconnect,
    sendEvent,
    stopAudioProcessing: cleanupAudioNodes,
    updatePlaybackStream,
    updateListeningState,
    updateThinkingState,
    isConnected,
    clientId,
    isUserSpeaking: false,
    stopVAD: () => {},
  };
};
